/* @img-migrated */
import React, { useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { ImagePlus, X, Loader2, GripVertical } from 'lucide-react';
import { toast } from 'sonner';

import Img from './Img';
import { resolveImg } from '../lib/images';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_BYTES = 5 * 1024 * 1024;

/**
 * Client-side image compressor.
 *  - Resizes to `maxEdge` px on the longest side.
 *  - Re-encodes as JPEG at `quality`.
 *  Preserves EXIF orientation via the browser's default handling of HTMLImageElement.
 */
async function compressImage(file, { maxEdge = 1600, quality = 0.82 } = {}) {
  if (!file.type.startsWith('image/')) return file;
  // Skip already-small images
  if (file.size < 350 * 1024) return file;

  const bitmap = await createImageBitmap(file).catch(() => null);
  const src = bitmap || (await new Promise((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = URL.createObjectURL(file);
  }));

  const w = src.width || src.naturalWidth;
  const h = src.height || src.naturalHeight;
  const scale = Math.min(1, maxEdge / Math.max(w, h));
  const outW = Math.round(w * scale);
  const outH = Math.round(h * scale);

  const canvas = document.createElement('canvas');
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(src, 0, 0, outW, outH);

  const blob = await new Promise((resolve) =>
    canvas.toBlob((b) => resolve(b || file), 'image/jpeg', quality)
  );
  if (!blob) return file;

  // If compression didn't help, keep original
  if (blob.size >= file.size) return file;

  return new File([blob], (file.name || 'image').replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' });
}

/**
 * MultiImageUploader — reusable multi-image picker with client-side compression,
 * drag-reorder, per-file progress, and max-count enforcement.
 *
 * Props:
 *   value           - string[] of uploaded image URLs (controlled)
 *   onChange        - (urls: string[]) => void
 *   max             - maximum images (default 5)
 *   endpoint        - absolute API endpoint for multi-upload (default community endpoint)
 *   label           - button label (default "Add Photos")
 *   size            - 'sm' | 'md' (default 'md')
 *   dataTestId      - test id prefix
 */
export const MultiImageUploader = ({
  value = [],
  onChange,
  max = 5,
  endpoint = `${API}/portal/community/upload-multi`,
  label = 'Add Photos',
  size = 'md',
  dataTestId = 'multi-uploader',
}) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const inputRef = useRef(null);

  const handleFiles = useCallback(async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith('image/'));
    if (files.length === 0) return;

    const remaining = max - value.length;
    if (remaining <= 0) {
      toast.error(`Maximum ${max} images reached`);
      return;
    }

    const batch = files.slice(0, remaining);
    if (files.length > remaining) {
      toast.info(`Only adding ${remaining} of ${files.length} images (max ${max})`);
    }

    setUploading(true);
    setProgress(0);
    try {
      // Compress each file in parallel
      const compressed = await Promise.all(batch.map((f) => compressImage(f).catch(() => f)));

      // Validate size after compression
      for (const f of compressed) {
        if (f.size > MAX_BYTES) {
          toast.error(`"${f.name}" exceeds 5 MB after compression. Please use a smaller image.`);
          setUploading(false);
          return;
        }
      }

      const fd = new FormData();
      compressed.forEach((f) => fd.append('files', f));

      const res = await axios.post(endpoint, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (evt) => {
          if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
        },
      });

      const urls = res.data?.image_urls || [];
      onChange([...value, ...urls]);
      if (urls.length > 0) toast.success(`${urls.length} image${urls.length !== 1 ? 's' : ''} added`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to upload images');
    } finally {
      setUploading(false);
      setProgress(0);
    }
  }, [value, max, endpoint, onChange]);

  const handleInputChange = (e) => {
    handleFiles(e.target.files);
    e.target.value = ''; // reset so same file can be re-selected
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  const removeAt = (idx) => onChange(value.filter((_, i) => i !== idx));

  const reorder = (from, to) => {
    if (from === to || from == null || to == null) return;
    const next = [...value];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onChange(next);
  };

  const atMax = value.length >= max;
  const btnBase = size === 'sm'
    ? 'px-2.5 py-1.5 text-[12px]'
    : 'px-3 py-2 text-[13px]';

  return (
    <div data-testid={dataTestId}>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleInputChange}
        className="hidden"
        data-testid={`${dataTestId}-input`}
      />

      {/* Dropzone / Add-photos button */}
      {!atMax && (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          className={`flex flex-col items-stretch gap-2 ${value.length === 0 ? 'rounded-xl border-2 border-dashed transition-colors ' + (dragOver ? 'border-[#002249] bg-[#002249]/5' : 'border-slate-200 hover:border-[#002249]/40') : ''}`}
        >
          {value.length === 0 ? (
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="flex flex-col items-center justify-center py-6 sm:py-8 px-4 text-center focus:outline-none"
              data-testid={`${dataTestId}-empty-btn`}
            >
              <ImagePlus className="w-6 h-6 sm:w-8 sm:h-8 text-[#65676B] mb-2" />
              <span className="text-[14px] font-semibold text-[#050505]">{uploading ? `Uploading ${progress}%` : label}</span>
              <span className="text-[12px] text-[#65676B] mt-0.5">
                Drag & drop or click &middot; up to {max} images &middot; 5 MB each
              </span>
            </button>
          ) : (
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className={`${btnBase} inline-flex items-center gap-1.5 rounded-lg text-[#002249] hover:bg-[#002249]/10 border border-[#002249]/30 font-semibold transition-colors self-start`}
              data-testid={`${dataTestId}-add-btn`}
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
              <span>{uploading ? `Uploading ${progress}%` : `Add more (${value.length}/${max})`}</span>
            </button>
          )}
        </div>
      )}

      {/* Previews — drag to reorder */}
      {value.length > 0 && (
        <div className="mt-3">
          <div className="flex items-center justify-between mb-2 px-0.5">
            <span className="text-[12px] font-medium text-[#65676B]" data-testid={`${dataTestId}-count`}>
              {value.length} / {max} image{value.length !== 1 ? 's' : ''}
              {value.length > 1 && <span className="ml-1 text-[11px] text-[#8C8C8C]">· drag to reorder</span>}
            </span>
            {atMax && <span className="text-[11px] font-semibold text-amber-600">Max reached</span>}
          </div>

          <div className={`grid gap-2 ${value.length === 1 ? 'grid-cols-1' : 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5'}`}>
            {value.map((img, i) => (
              <div
                key={`${img}-${i}`}
                draggable
                onDragStart={() => setDragIndex(i)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => { reorder(dragIndex, i); setDragIndex(null); }}
                onDragEnd={() => setDragIndex(null)}
                className={`relative group aspect-square rounded-lg overflow-hidden bg-slate-100 border transition-all ${dragIndex === i ? 'opacity-50 scale-95' : 'border-slate-200'}`}
                data-testid={`${dataTestId}-thumb-${i}`}
              >
                <Img variant="generic"
                  src={resolveImg(img)}
                  alt={`Upload ${i + 1}`}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  decoding="async"
                />
                {/* Order badge */}
                <span className="absolute top-1.5 left-1.5 w-5 h-5 rounded-full bg-black/70 text-white text-[10px] font-bold flex items-center justify-center">
                  {i + 1}
                </span>
                {/* Reorder indicator */}
                {value.length > 1 && (
                  <span className="absolute bottom-1.5 left-1.5 w-6 h-6 rounded-md bg-black/50 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-grab">
                    <GripVertical className="w-3.5 h-3.5" />
                  </span>
                )}
                {/* Remove */}
                <button
                  type="button"
                  onClick={() => removeAt(i)}
                  className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-black/70 hover:bg-black/90 text-white flex items-center justify-center"
                  aria-label="Remove image"
                  data-testid={`${dataTestId}-remove-${i}`}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>

          {uploading && (
            <div className="mt-2 h-1 rounded-full bg-slate-100 overflow-hidden">
              <div
                className="h-full bg-[#002249] transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MultiImageUploader;
